import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, ArrowRight, DollarSign, Percent, TrendingUp, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export default function CalculatorPage() {
  const [values, setValues] = useState({
    salePrice: 2000000,
    originalPurchase: 1000000,
    improvementCosts: 100000,
    yearsOwned: 10,
    depreciationTaken: 200000,
    federalTaxRate: 20,
    stateTaxRate: 5,
  });

  const [showResults, setShowResults] = useState(false);

  // Calculate gains and taxes
  const calculateResults = () => {
    const { salePrice, originalPurchase, improvementCosts, depreciationTaken, federalTaxRate, stateTaxRate } = values;
    
    // Adjusted cost basis
    const costBasis = originalPurchase + improvementCosts - depreciationTaken;
    
    // Capital gain
    const capitalGain = salePrice - costBasis;
    
    // Depreciation recapture (taxed at 25%)
    const depreciationRecapture = depreciationTaken;
    const depreciationRecaptureTax = depreciationRecapture * 0.25;
    
    // Long-term capital gains tax
    const netCapitalGain = capitalGain - depreciationRecapture;
    const capitalGainsTax = Math.max(0, netCapitalGain) * (federalTaxRate / 100);
    
    // State taxes
    const stateTax = capitalGain * (stateTaxRate / 100);
    
    // Net Investment Income Tax (3.8% for high earners)
    const niit = capitalGain * 0.038;
    
    // Total taxes owed
    const totalTaxes = depreciationRecaptureTax + capitalGainsTax + stateTax + niit;
    
    // Amount preserved through 1031
    const taxSavings = totalTaxes;
    
    // Compound growth potential (assuming 7% annual return over 10 years)
    const futureValue = taxSavings * Math.pow(1.07, 10);
    const additionalWealth = futureValue - taxSavings;

    return {
      costBasis,
      capitalGain,
      depreciationRecapture,
      depreciationRecaptureTax,
      capitalGainsTax,
      stateTax,
      niit,
      totalTaxes,
      taxSavings,
      futureValue,
      additionalWealth,
    };
  };

  const results = calculateResults();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline mb-4 block">Investment Tool</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-medium tracking-tight text-[#1A1A2E] leading-[1.1] mb-6" data-testid="calculator-title">
              1031 Exchange Tax Savings Calculator
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Estimate how much you could save in taxes by completing a 1031 exchange instead of a traditional sale. See the potential long-term impact on your capital.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Input Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-2xl font-bold text-[#1A1A2E] mb-8">Property Details</h2>
              
              <div className="space-y-8">
                {/* Sale Price */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      Expected Sale Price
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-4 h-4 text-slate-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">The price you expect to sell your property for.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <span className="text-lg font-medium text-[#1A1A2E]">{formatCurrency(values.salePrice)}</span>
                  </div>
                  <Slider
                    value={[values.salePrice]}
                    onValueChange={(v) => handleChange("salePrice", v[0])}
                    min={500000}
                    max={20000000}
                    step={50000}
                    className="w-full"
                    data-testid="sale-price-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>$500K</span>
                    <span>$20M</span>
                  </div>
                </div>

                {/* Original Purchase Price */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-700">Original Purchase Price</label>
                    <span className="text-lg font-medium text-[#1A1A2E]">{formatCurrency(values.originalPurchase)}</span>
                  </div>
                  <Slider
                    value={[values.originalPurchase]}
                    onValueChange={(v) => handleChange("originalPurchase", v[0])}
                    min={100000}
                    max={values.salePrice}
                    step={25000}
                    className="w-full"
                    data-testid="purchase-price-slider"
                  />
                </div>

                {/* Improvement Costs */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-700">Capital Improvements</label>
                    <span className="text-lg font-medium text-[#1A1A2E]">{formatCurrency(values.improvementCosts)}</span>
                  </div>
                  <Slider
                    value={[values.improvementCosts]}
                    onValueChange={(v) => handleChange("improvementCosts", v[0])}
                    min={0}
                    max={500000}
                    step={10000}
                    className="w-full"
                  />
                </div>

                {/* Depreciation Taken */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      Depreciation Taken
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-4 h-4 text-slate-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Total depreciation deductions claimed. This is recaptured at 25% upon sale.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <span className="text-lg font-medium text-[#1A1A2E]">{formatCurrency(values.depreciationTaken)}</span>
                  </div>
                  <Slider
                    value={[values.depreciationTaken]}
                    onValueChange={(v) => handleChange("depreciationTaken", v[0])}
                    min={0}
                    max={500000}
                    step={10000}
                    className="w-full"
                  />
                </div>

                {/* Tax Rates */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">Federal Cap Gains Rate (%)</label>
                    <Input
                      type="number"
                      value={values.federalTaxRate}
                      onChange={(e) => handleChange("federalTaxRate", parseFloat(e.target.value) || 0)}
                      className="h-12"
                      min={0}
                      max={37}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">State Tax Rate (%)</label>
                    <Input
                      type="number"
                      value={values.stateTaxRate}
                      onChange={(e) => handleChange("stateTaxRate", parseFloat(e.target.value) || 0)}
                      className="h-12"
                      min={0}
                      max={15}
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => setShowResults(true)} 
                  className="btn-primary w-full py-6 text-base rounded-sm"
                  data-testid="calculate-btn"
                >
                  <Calculator className="mr-2 w-5 h-5" />
                  Calculate Tax Savings
                </Button>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-[#1A1A2E] mb-8">Your Tax Savings</h2>

              {/* Main Savings Card */}
              <div className="bg-[#1A1A2E] p-8 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="w-8 h-8 text-[#60A5FA]" />
                  <span className="text-slate-400">Estimated Tax Savings</span>
                </div>
                <p className="text-5xl font-bold text-white mb-2">
                  {formatCurrency(results.taxSavings)}
                </p>
                <p className="text-slate-400 text-sm">
                  Capital preserved through 1031 exchange
                </p>
              </div>

              {/* Future Value Card */}
              <div className="bg-slate-50 p-8 border border-slate-100 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="w-6 h-6 text-[#0066FF]" />
                  <span className="text-slate-600">10-Year Compound Growth Potential</span>
                </div>
                <p className="text-3xl font-bold text-[#1A1A2E] mb-2">
                  {formatCurrency(results.futureValue)}
                </p>
                <p className="text-slate-500 text-sm">
                  Assuming 7% annual return on preserved capital
                </p>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Additional capital preserved:</span>
                    <span className="font-medium text-green-600">+{formatCurrency(results.additionalWealth)}</span>
                  </div>
                </div>
              </div>

              {/* Tax Breakdown */}
              <div className="bg-white p-6 border border-slate-100">
                <h3 className="text-lg font-medium text-[#1A1A2E] mb-4">Tax Breakdown (Without 1031)</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Capital Gain</span>
                    <span className="font-medium">{formatCurrency(results.capitalGain)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Depreciation Recapture Tax (25%)</span>
                    <span className="font-medium text-red-600">{formatCurrency(results.depreciationRecaptureTax)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Federal Capital Gains Tax</span>
                    <span className="font-medium text-red-600">{formatCurrency(results.capitalGainsTax)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">State Tax</span>
                    <span className="font-medium text-red-600">{formatCurrency(results.stateTax)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Net Investment Income Tax (3.8%)</span>
                    <span className="font-medium text-red-600">{formatCurrency(results.niit)}</span>
                  </div>
                  <div className="flex justify-between py-3 bg-red-50 -mx-6 px-6">
                    <span className="font-medium text-red-700">Total Taxes Owed</span>
                    <span className="font-bold text-red-700">{formatCurrency(results.totalTaxes)}</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-slate-400 mt-6">
                * This calculator provides estimates for illustrative purposes only. Actual tax liability depends on individual circumstances. Consult a qualified tax professional for advice specific to your situation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-4">
              Benefits of a 1031 Exchange
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Defer All Taxes", desc: "Defer 100% of capital gains, depreciation recapture, and state taxes when exchanging into like-kind property." },
              { title: "Compound Growth", desc: "Keep more capital working for you. Money not paid in taxes can continue to be deployed into additional real estate." },
              { title: "Portfolio Repositioning", desc: "Exchange into different assets, locations, or property types that align with your goals." },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className="p-8 bg-white border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <CheckCircle2 className="w-8 h-8 text-[#0066FF] mb-4" />
                <h3 className="text-xl font-medium text-[#1A1A2E] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your 1031 Exchange?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Connect with our team to explore luxury condo unit replacement properties in NYC and Miami.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/properties">
                <Button className="bg-white text-[#1A1A2E] hover:bg-slate-100 px-8 py-6 text-base rounded-sm">
                  View Properties
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base rounded-sm">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
